<template>
  <PageWrapper
    :active-page="PAGE_COMMUNITY_COMMUNITY"
    :active-section="SECTION_COMMUNITY"
    :breadcrumbs="breadcrumbs"
    align="left"
    page-name="Profile"
    sub-title=""
    width="fixed"
  >
    <ProfileWrapper
      :logo-image="orgProfileData.logo_url_override || orgProfileData.logo_url"
      :splash-image="orgProfileData.splash_url_override || orgProfileData.splash_url"
      :title="orgProfileData.name"
    >
      <template #no-edit-title>
        <iSubHeading>
          <iRow vertical-align="middle" width="hug">
            {{ orgProfileData.name }}
            <iImageContainer
              v-if="hasPublicationCapability"
              v-tooltip="'Creator'"
              :height="30"
              src="/images/influencer-icon.png"
            />
            <iImageContainer
              v-if="hasAdvertiserCapability"
              v-tooltip="'Advertiser'"
              :height="30"
              src="/images/advertiser-icon.png"
            />
          </iRow>
          <iRow v-if="tags.length">
            <iChip
              v-for="tag in tags"
              :key="tag"
              variant="secondary"
              :show-icon="false"
              size="small"
            >
              {{ tag }}
            </iChip>
          </iRow>
        </iSubHeading>
      </template>
      <template #menu>
        <iRow vertical-align="middle" width="hug" wrap="nowrap">
          <iButton
            v-if="ownerId && (hasClaimedAdvertiserCapability || hasPublicationCapability)"
            size="small"
            variant="secondary"
            @click="goToMessagePage"
          >
            Message
          </iButton>
          <iButton
            v-if="hasPublicationCapability && ownerId !== creatorOrgId"
            size="small"
            variant="secondary"
            @click="showInviteToCollaborateModal = true"
          >
            Collaborate
          </iButton>
          <MyBrandsButton v-if="hasAdvertiserCapability && orgProfileData.domains.length" :creator-org-id="creatorOrgId" :advertiser-org-id="orgId" />
        </iRow>
      </template>
      <iSpace :height="1" />
      <iRow v-if="details" width="fill">
        <iText variant="subtle">
          {{ details }}
        </iText>
      </iRow>
      <iColumn gap="large">
        <iColumn v-if="hasAdvertiserCapability && orgProfileData.domains.length" gap="small">
          <iSubHeading font-size="extraLarge" variant="standard">
            Advertiser Websites
          </iSubHeading>
          <iText>
            Visit these websites to find products you may want to recommend to your followers.
          </iText>
          <iColumn gap="extraSmall">
            <iLink
              v-for="(domain, index) in orgProfileData.domains"
              :key="index"
              :href="formatDomain(domain.domain)"
              target="_blank"
            >
              {{ domain.domain }}
            </iLink>
          </iColumn>
        </iColumn>
        <iColumn gap="large">
          <iColumn v-if="hasAdvertiserCapability && orgProfileData.payouts_and_referral_periods.length" gap="small">
            <iSubHeading font-size="extraLarge" variant="standard">
              Affiliate Payout
            </iSubHeading>
            <iColumn gap="extraSmall">
              <iText v-for="payoutString in payouts" :key="payoutString">
                {{ payoutString }}
              </iText>
            </iColumn>
          </iColumn>
          <OrgTeam v-if="activeUsers.length > 0" :org-id="orgId" />
          <iDataTable
            v-if="isAdvertiser"
            :fields="fields"
            :has-more="false"
            :items="advertiserDetails.domains"
            primary-key="advertiser_org_id"
          >
            <template #td(domain)="{data}">
              <iLink :href="getNonRelativeDomain(data.domain)" target="_blank">
                {{ data.domain }}
              </iLink>
            </template>
          </iDataTable>
          <iColumn v-if="orgProfilePublications.length > 0" gap="none">
            <iSubHeading>
              Publications
            </iSubHeading>
            <iDataTable
              :fields="publicationFields"
              :has-more="false"
              :items="activePublications"
              primary-key="publication_id"
            >
              <template #td(domain)="slotProps">
                <iLink :wrap="false" :href="getNonRelativeDomain(slotProps.data.domain)" target="_blank">
                  {{ cleanDomain(slotProps.data.domain) }}
                </iLink>
              </template>
              <template #td(actions)="slotProps">
                <iColumn align="right">
                  <ApplyToCollaborate
                    :org-id="orgId"
                    :publication-id="slotProps.data.publication_id"
                    apply-button-text="Apply To Collaborate"
                  />
                </iColumn>
              </template>
            </iDataTable>
          </iColumn>
          <OrgAreasOfExpertise :org-id="orgId" />
          <OrgServicePackages :org-id="orgId" />
          <InviteToCollaborateOnPlatform
            :show-modal="showInviteToCollaborateModal"
            :org-id="orgId"
            :org-name="orgProfileData.name"
            @close="onCloseInviteToCollaborateModal"
          />
        </iColumn>
      </icolumn>
    </ProfileWrapper>
  </PageWrapper>
</template>

<script>
import PageWrapper from "@/components/PageWrapper";
import {
  SECTION_COMMUNITY,
  PAGE_COMMUNITY_COMMUNITY,
} from "@/constants/nav-constants";
import { mapState } from "pinia";
import { useOrganizationStore } from "@/stores/organization-store";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import OrgTeam from "@/components/directory/OrgTeam.vue";
import ApplyToCollaborate from "@/components/publication/ApplyToCollaborate.vue";
import ProfileWrapper from "@/components/publication/ProfileWrapper";
import InviteToCollaborateOnPlatform from "@/components/publication/InviteToCollaborateOnPlatform.vue";
import MyBrandsButton from "@/components/MyBrandsButton.vue";
import OwnerNotFoundError from "@/errors/OwnerNotFoundError";
import OrgAreasOfExpertise from "@/components/publication/OrgAreasOfExpertise.vue";
import OrgServicePackages from "@/components/publication/OrgServicePackages.vue";

export default {
  name: "OrgProfilePage",
  components: {
    OrgServicePackages,
    OrgAreasOfExpertise,
    MyBrandsButton,
    InviteToCollaborateOnPlatform,
    ProfileWrapper,
    ApplyToCollaborate,
    OrgTeam,
    PageWrapper,
  },
  props: {
    orgId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      SECTION_COMMUNITY,
      PAGE_COMMUNITY_COMMUNITY,
      fields: [
        {
          key: "domain",
          label: "Domains",
        },
        {
          key: "rate",
          label: "Rate",
        },
      ],
      showInviteToCollaborateModal: false,
    };
  },
  computed: {
    details() {
      return this.orgProfileData.org_description_override || this.orgProfileData.org_description;
    },
    breadcrumbs() {
      return [
        { url: "/", label: "Home" },
        { url: "/community", label: "Community" },
      ];
    },
    ...mapState(useOrganizationStore, [
      "loadingOrgDetails",
      "advertiserDetails",
      "loadingAdvertiserDetails",
      "orgProfileData",
      "hasPublicationCapability",
      "hasAdvertiserCapability",
      "hasClaimedAdvertiserCapability",
      "activeUsers",
      "getOrgData",
    ]),
    ...mapState(useCreatorPublicationStore, [
      "orgProfilePublications",
    ]),
    creatorOrgId() {
      return this.getOrgData.org_id;
    },
    isAdvertiser() {
      return this.orgProfileData.domains > 0;
    },
    activePublications() {
      return this.orgProfilePublications.filter(publication => publication.status === "active");
    },
    tags() {
      return this.orgProfileData.tags ? this.orgProfileData.tags.map(tagObj => tagObj.tag) : [];
    },
    publicationFields() {
      const fields = [
        {
          key: "name",
          label: "Name",
        },
        {
          key: "domain",
          label: "Domain",
        },
      ];

      if (this.orgId !== this.getOrgData.org_id) {
        fields.push({
          key: "actions",
          label: "",
        });
      }

      return fields;
    },
    payouts() {
      return this.orgProfileData.payouts_and_referral_periods.map(row => {
        const min = row.payout_type === "percentage" ? `${row.payout_value_min}%` : `$${row.payout_value_min}`;
        const max = row.payout_type === "percentage" ? `${row.payout_value_max}%` : `$${row.payout_value_max}`;
        return min === max ? min : `${min} - ${max}`;
      });
    },
    ownerId() {
      const team = useOrganizationStore().activeUsers;
      const owner = team.filter(user => parseInt(user.role_id) === 0)[0];
      return owner?.user_id;
    },
  },
  methods: {
    goToMessagePage() {
      if (!this.ownerId) {
        throw new OwnerNotFoundError("Owner not found on Profile Page to message", {
          orgId: this.orgId,
        });
      }
      window.location.href = `/community/user/${this.ownerId}/message`;
    },
    onCloseInviteToCollaborateModal() {
      this.showInviteToCollaborateModal = false;
    },
    getNonRelativeDomain(domain) {
      return `//${domain}`;
    },
    formatDomain(domain) {
      if (domain.startsWith("http", 0)) {
        return domain;
      }
      return `https://${domain}`;
    },
    cleanDomain(domain) {
      let formatted = domain.replace("http://", "");
      formatted = formatted.replace("https://", "");

      return formatted;
    },
  },
};
</script>

<style lang="scss" scoped>
.mobile-display {
  display: none;
  @include phone {
    display: block;
  }
}

.not-mobile-display {
  display: block;
  @include phone {
    display: none;
  }
}
</style>
